/**
 * LE PLAN DU ZOO: un pavillon et ses enclos, en fractions de l'emprise.
 *
 * POURQUOI CE FICHIER REMPLACE UN ALGORITHME. Un enclos etait, jusqu'ici, une
 * aire que le joueur fermait lui-meme a la cloture, reconnue par un
 * remplissage par diffusion sur six mille quatre cents cases
 * (`analyserEnclos`, supprime). C'etait joli sur le papier et penible en jeu:
 * `MARGE_BATIMENT` interdit deux clotures bout a bout, donc une barriere reelle
 * est toujours trouee d'un metre; il a fallu inventer `MARGE_CLOISON` pour
 * refermer ces trous-la, et le resultat restait un jeu d'adresse ou l'on
 * s'entendait repondre « il reste un passage » sans savoir lequel. Le zoo se
 * POSE maintenant d'un seul geste, avec ses enclos deja dessines.
 *
 * Les nombres sont des FRACTIONS de l'emprise, exactement comme `PLAN_ETAGE` et
 * `ANCRAGE_MEUBLE`: une seule table, une seule rotation appliquee ensuite par
 * `tourner`. Ecrire les quatre quarts de tour a la main est ce qui avait deja
 * mis la porte du mauvais cote.
 *
 * CONTRAINTE QUI COMMANDE TOUS LES CHIFFRES. Les bandes de murs ne doivent
 * jamais se chevaucher: le mur d'enceinte du zoo pousse vers le cote le plus
 * proche, chaque enclos pousse vers l'exterieur, et deux bandes qui se
 * croiseraient feraient faire la navette au bonhomme un tick sur deux — des
 * deux cotes du fil, donc sans le moindre recalage pour le signaler. C'est le
 * piege deja paye sur la terrasse de la villa. Avec l'emprise de 15 x 11 m:
 *
 *   - enceinte: sa bande commence a 6,62 m du centre en x et a 4,62 m en z
 *     (demi-emprise moins EPAISSEUR_MUR moins PLAYER_RADIUS);
 *   - l'enclos le plus a l'exterieur pousse jusqu'a 6,30 m en x et 4,40 m en z;
 *   - entre deux enclos voisins il reste 90 cm, dont 70 sont manges par les
 *     deux poussees: 20 cm de bande franche.
 *
 * POURQUOI L'EMPRISE EST SI SERREE. Le zoo a d'abord fait 22 x 16 m, ce qui
 * etait confortable sur le papier et impossible en jeu: `validerPlacement`
 * refuse un noeud de ressource sous l'emprise, et un balayage du monde entier
 * a mesure ZERO emplacement en foret et 3,8 % sur la plage. A 15 x 11 il se
 * pose aussi souvent que la villa a etage, qui est le plus gros ouvrage
 * existant — c'est la barre qu'on s'est donnee, parce qu'un batiment qu'on ne
 * peut pas poser est un batiment qui n'existe pas.
 *
 * Les marges sont donc MINCES, et `tests/unit/zoo.test.ts` ne fait pas
 * confiance a ce calcul: il balaie toute l'emprise, sous les quatre rotations,
 * et exige que `resoudreMurs` soit un POINT FIXE.
 */
import type { BatimentKind } from '../types/monde.ts'
import { CHAMBRES_HOTEL } from './hotel.ts'
import { PIECES_PARC } from './parc-aquatique.ts'

/**
 * Une piece cloisonnee a l'interieur d'une emprise.
 *
 * `x`, `z`, `dl`, `dp` sont en fractions de l'emprise (`dl` et `dp` sont des
 * DEMI-dimensions). `porte` est en metres, sur la face -z de la piece — la
 * meme convention que les maisons et que le bureau de la terrasse.
 */
export interface PieceInterieure {
  /** Nom affiche au joueur, accentue. */
  nom: string
  x: number
  z: number
  dl: number
  dp: number
  /** Largeur de l'ouverture, en metres, sur la face -z de la piece. */
  porte: number
  /**
   * Vrai si la piece est un ENCLOS: un animal peut y etre installe.
   *
   * Le drapeau existe depuis que l'hotel a des chambres: ses cloisons passent
   * par le meme `PLAN_INTERIEUR`, et sans lui `enclosAuPoint` aurait reconnu
   * une chambre d'hotel comme un enclos — on y aurait « installe » un lapin au
   * pied du lit. C'est la DONNEE qui distingue une chambre d'un enclos, jamais
   * le nom du modele.
   */
  enclos?: boolean
  /**
   * Vrai si la piece contient un BASSIN: un canard peut y vivre.
   *
   * Il le faut. Les especes d'eau (`milieu` 'eau' ou 'surface') vivaient dans
   * une aire que le joueur fermait autour d'une mare; les enclos du zoo sont a
   * sec, et sans bassin il n'y avait plus UN SEUL endroit au monde ou installer
   * un canard apprivoise. C'est le bassin qui repond, pas le terrain — comme le
   * spa de la terrasse, qui est de l'eau sans que le relief le soit.
   */
  bassin?: boolean
}

/**
 * Nombre de pensionnaires par enclos.
 *
 * Un chiffre fixe, et non plus une surface divisee par 12 m²: les enclos ont
 * tous la meme taille maintenant, donc le calcul aurait rendu trois fois le
 * meme nombre en donnant l'illusion qu'agrandir change quelque chose.
 *
 * Trois par enclos, neuf en tout: c'est deja plus que les six que pouvait
 * contenir l'unique enclos dessine a la cloture, et un enclos de 3,6 x 4,4 m
 * ou l'on entassait quatre betes se lisait comme une cage.
 */
export const CAPACITE_ENCLOS = 3

/**
 * Le plan du zoo: trois enclos alignes au fond — dont un BASSIN — et l'allee
 * des visiteurs devant.
 *
 * PAS DE PAVILLON. Il y en a eu un, et il n'apportait rien qu'une contrainte:
 * une quatrieme boite a tenir a l'ecart des trois autres et de l'enceinte, dans
 * une emprise qu'on venait justement de resserrer. Le batiment, c'est
 * l'ENCEINTE — un mur qui fait le tour, une seule entree large au milieu de sa
 * facade — et ce qu'on y visite, ce sont les enclos.
 *
 * Les trois enclos ouvrent tous sur la MEME allee, celle qui longe leur face
 * -z: on n'a jamais a se faufiler entre deux enclos, et c'est ce qui autorise
 * de les serrer autant.
 *
 * Emprise 15 x 11 m. Demi-dimensions 1,8 x 2,2 m, centres a x = -4,5 / 0 / +4,5
 * et z = +2,2. L'allee occupe donc toute la largeur, sur 4,3 m de profondeur.
 */
export const PLAN_ZOO: readonly PieceInterieure[] = Object.freeze([
  { nom: 'Enclos de gauche', x: -0.3, z: 0.2, dl: 0.12, dp: 0.2, porte: 1.2, enclos: true },
  { nom: 'Enclos du milieu', x: 0, z: 0.2, dl: 0.12, dp: 0.2, porte: 1.2, enclos: true },
  // Celui de droite a le BASSIN: c'est le seul ou un canard peut vivre, et
  // c'est le seul endroit du monde ou il le peut depuis que les enclos ne se
  // dessinent plus autour d'une mare.
  { nom: 'Bassin', x: 0.3, z: 0.2, dl: 0.12, dp: 0.2, porte: 1.2, bassin: true, enclos: true },
])

/**
 * LES CLOISONS INTERIEURES DE CHAQUE MODELE.
 *
 * Partiel a dessein, et interroge par la DONNEE plutot que par le nom du
 * modele: le jour ou un second batiment aura des pieces cloisonnees, il n'y
 * aura rien a retrouver dans `resoudreMurs`. C'est la lecon de « une regle
 * ecrite en dur pour UN modele oublie toujours les suivants » — la cueillette
 * testait `kind !== 'jardin'` a trois endroits, et le potager ne donnait rien.
 */
export const PLAN_INTERIEUR: Partial<Record<BatimentKind, readonly PieceInterieure[]>> = Object.freeze({
  zoo: PLAN_ZOO,
  // Les chambres du rez-de-chaussee de l'hotel: des pieces, pas des enclos —
  // elles n'ont pas le drapeau `enclos`, et c'est ce qui empeche d'y installer
  // une bete apprivoisee au pied du lit.
  hotel: CHAMBRES_HOTEL,
  // Le pavillon du parc aquatique: un vestiaire ou l'on entre, sans drapeau
  // `enclos` lui non plus — un lapin ne vit pas dans un vestiaire.
  parc_aquatique: PIECES_PARC,
})
